import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
    NotificationMessage,
    VehicleService,
} from '@polaris-customerweb/sharedcomponents.garage';
import {
    ConfirmDialogComponent,
    ConfirmDialogOptions,
    LogService,
} from '@polaris-customerweb/sharedcomponents.shared';
import { ContentService } from 'src/app/services/content.service';

@Injectable({ providedIn: 'root' })
export class NotificationNavigationService {
    readonly className = 'NotificationNavigationService';

    private vehicleDeletedDialogOptions = new ConfirmDialogOptions({
        confirmButtonId: 'notificationDoesNotExistConfirm',
        contentText: $localize`This vehicle is no longer in your garage`,
        dialogId: 'notificationErrorMessage',
        hideCancelButton: true,
        titleText: $localize`Vehicle not found`,
    });

    private rideNotAvailableDialogOptions = new ConfirmDialogOptions({
        confirmButtonId: 'notificationDoesNotExistConfirm',
        contentText: $localize`The tracked ride you selected is no longer available`,
        dialogId: 'notificationErrorMessage',
        hideCancelButton: true,
        titleText: $localize`Ride selected is no longer available`,
    });

    constructor(
        private readonly contentService: ContentService,
        private readonly logService: LogService,
        private readonly matDialog: MatDialog,
        private readonly router: Router,
        private readonly vehicleService: VehicleService
    ) {}

    navigateToNotification(message: NotificationMessage): boolean {
        switch ((message.notificationType as string).toLowerCase()) {
            case 'postridereport':
                return this.navToRide(message);
            case 'otaupdate':
                return this.navToOta(message);
            case 'mileagebasedmaintenance':
                return this.navToMaintenance(message);
            case 'diagnostictroublecode':
            case 'lowbattery':
            case 'prehibernationalert':
                return this.navToHome(message, true);
            case 'harmandeviceremoved':
            case 'harmandeviceinstalled':
                return this.navToHome(message, false);
            case 'bumpalert':
            case 'geofence':
            case 'geofencebreach':
            case 'sosalert':
                return this.navToAlert(message);
            default:
                this.logService.warn(
                    this.className,
                    'unknown notification type'
                );
                return false;
        }
    }

    private navToRide(message: NotificationMessage): boolean {
        const id =
            message.properties['rideId'] || message.properties['trackId'];
        void this.router.navigate(['/map', 'tracked-ride', id]);
        if (!id) {
            this.matDialog.open(ConfirmDialogComponent, {
                data: this.rideNotAvailableDialogOptions,
            });
        }
        return true;
    }

    private navToOta(message: NotificationMessage): boolean {
        this.contentService
            .getCheckInMessage(message.properties.cmsId)
            .subscribe({
                error: (error: unknown) => {
                    this.logService.error(this.className, error);
                },
                next: (checkInMessage) => {
                    this.matDialog.open(ConfirmDialogComponent, {
                        data: new ConfirmDialogOptions({
                            confirmButtonId: 'notificationOtaDialogConfirm',
                            contentText: checkInMessage.html,
                            dialogId: 'notificationOtaDialog',
                            hideCancelButton: true,
                            titleText: checkInMessage.title,
                        }),
                    });
                },
            });
        return false;
    }

    private navToMaintenance(message: NotificationMessage): boolean {
        const vehicleId =
            this.vehicleService.$vinToVehicleMap()[message.properties['vin']]
                ?.id;
        if (vehicleId) {
            const intervalKey = message.body.match(
                /[0-9]+-miles-[0-9]+-km/g
            )?.[0];
            void this.router.navigate(
                ['/maintenance', 'vehicle', vehicleId, 'maintenance-schedule'],
                {
                    queryParams: {
                        intervalKey,
                    },
                }
            );
            return true;
        } else {
            this.matDialog.open(ConfirmDialogComponent, {
                data: this.vehicleDeletedDialogOptions,
            });
            return false;
        }
    }

    private navToHome(
        message: NotificationMessage,
        showTelemetry: boolean
    ): boolean {
        const vehicleId =
            this.vehicleService.$vinToVehicleMap()[message.properties['vin']]
                ?.id;
        if (vehicleId) {
            void this.router.navigate(['/home'], {
                queryParams: {
                    showTelemetry,
                    vehicleId,
                },
            });
            return true;
        } else {
            this.matDialog.open(ConfirmDialogComponent, {
                data: this.vehicleDeletedDialogOptions,
            });
            return false;
        }
    }

    private navToAlert(message: NotificationMessage): boolean {
        const vehicleId =
            this.vehicleService.$vinToVehicleMap()[message.properties['vin']]
                ?.id;
        if (vehicleId) {
            void this.router.navigate([
                '/map',
                'vehicle',
                vehicleId,
                'alert',
                message.id,
            ]);
            return true;
        } else {
            this.matDialog.open(ConfirmDialogComponent, {
                data: this.vehicleDeletedDialogOptions,
            });
            return false;
        }
    }
}
